import React, {useRef, useState} from "react";
import {useSelector} from "react-redux";
import * as reduxSelectors from "components/store/application.reducers";
import {Store} from "redux";
import {AttachmentsForRender} from "types/attachment";
import AttachmentRenderer from "./AttachmentRenderer";
import "./styles/bypassCIR.scss";
import "./styles/tabs.dark.scss";
import CollapsableTabs from "./CollapsableTabs";
import BypassCIRSkeleton from "./BypassCIR.Skeleton";
import MinMaxGroup from "components/taskdetails-remake/panels/attachments/MinMaxGroup";
import {launchDarkly} from "utils/launchDarkly";
import NewCollapsableTabsComponent from "components/taskdetails-remake/panels/attachments/NewCollapsableTabs.component";


interface BypassCIRProps {
    isAttachmentPanelMaxWidth: Boolean;
    isAttachmentPanelWidthDefault: Boolean;
    maximizePanel: React.FC;
    minimizePanel: React.FC;
    ownerDocument?: any;
    uid?: string | null;
    docVersion: string | number | null;
}

export const BypassCIRContainer = ({...props}: BypassCIRProps): React.ReactElement => {
    const attachments: Array<AttachmentsForRender> = useSelector((store: Store) => reduxSelectors.getTaskAttachmentsForRender(store));
    const isPreviewDetached = useSelector((store: Store) => reduxSelectors.isDocumentPreviewDetached(store));
    const tabsRefs = useRef<(HTMLDivElement | null)[]>([]);
    let selectedDocument = useSelector((store: Store) => reduxSelectors.getSelectedDocument(store));
    let activeTab: number | null = selectedDocument.index;

    const localStorageValue = window.localStorage.getItem(`@@attinfo/is-panel-collapsed`) ? window.localStorage.getItem(`@@attinfo/is-panel-collapsed`).toLowerCase() === "true" : true;
    const [isPanelCollapsed, setIsPanelCollapsed] = useState<boolean>(localStorageValue);

    const useNewGaiaTabs = launchDarkly.userClient?.variation("new-gaia-tabs", false);

    const setActiveTab = (value: number) => {
        tabsRefs.current.forEach((ref, i) => {
            if (!ref) return;
            activeTab = value;
            ref.classList.toggle('active', value === i);
            ref.classList.toggle('hidden', value !== i);
        });
    }

    const collapseCallback = () => {
        window.localStorage.setItem(`@@attinfo/is-panel-collapsed`, !isPanelCollapsed);
        setIsPanelCollapsed(!isPanelCollapsed);
    }

    const tabContent: React.ReactElement[] | React.ReactElement = attachments && Array.isArray(attachments) ? attachments.map((attachment: AttachmentsForRender, index: number) => {
        return activeTab === index ? <div
            className={`attachments-preview-modal__tab-content  ${(activeTab === index) ? "active" : "hidden"}`}
            ref={ref => tabsRefs.current[index] = ref}
            role="tabpanel"
            aria-labelledby="selected-tab"
            key={`${attachment.id}_tab-pane`}
            aria-hidden={index === activeTab ? "false" : "true"}>
            {!isPreviewDetached && <MinMaxGroup {...props} attachment={attachment}/>}
            <AttachmentRenderer attachment={attachment}
                                uid={props.uid}
                                docVersion={props.docVersion}
                                ownerDocument={props.ownerDocument}
                                isPanelCollapsed={isPanelCollapsed}
                                collapseCallback={collapseCallback}/>
        </div> : <div key={"not-active-tab_" + index}></div>;
    }) : <div key={"no-items"}></div>;

    if (!attachments || !Array.isArray(attachments) || attachments.length === 0) return (<BypassCIRSkeleton/>);
    if (attachments?.length === 1)
        return (<div className={`h-100 bg-white ${isPreviewDetached ? "" : "no-tabs"}`} data-testid={"__tst_no_tabs"}>
            <div className={"no-tabs__wrap rounded-4 h-100"}>
                {!isPreviewDetached && <MinMaxGroup {...props} attachment={attachments[0]}/>}
                <AttachmentRenderer attachment={attachments[0]}
                                    ownerDocument={props.ownerDocument}
                                    isPanelCollapsed={isPanelCollapsed}
                                    uid={props.uid}
                                    docVersion={props.docVersion}
                                    collapseCallback={collapseCallback}/>
            </div>
        </div>);

    return (
        <>
            {useNewGaiaTabs ?
                <NewCollapsableTabsComponent attachments={attachments}
                                             activeTab={activeTab}
                                             setActiveTab={setActiveTab}/> :
                <CollapsableTabs attachments={attachments}
                                 activeTab={activeTab}
                                 setActiveTab={setActiveTab}/>
            }
            {tabContent}
        </>
    )
}

export default BypassCIRContainer;

